import { Mutex } from "async-mutex";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { authTokenChange, setUserDetails, logoutUser } from "../slices/authSlice";
import { BackendId } from "@libs/appliance_common";
// Shared by all callers
export const mutex = new Mutex();
const rawBaseQuery = fetchBaseQuery({
    baseUrl: "/"
});
export const handleTokenRefresh = async (api)=>{
    // Use the mutex to ensure only one refresh request happens at a time.
    //
    // The reason this is necessary is because refresh tokens are invalidated
    // after use -- a few concurrent token refresh calls would mean that whichever
    // caller gets here first successfully fetches a new access+refresh token
    // pair, leaving the remaining callers to attempt a token refresh with an
    // invalidated refresh token.
    const release = await mutex.acquire();
    let refreshed = false;
    try {
        const authState = api.getState().auth;
        // OIDC users hold a Keycloak JWT as their refresh token.  Exchange it
        // directly with Keycloak's token endpoint (grant_type=refresh_token)
        // instead of the local /refresh route which expects a UUID.
        if (authState.currentUser?.user?.backend === BackendId.OIDC) {
            refreshed = await handleKeycloakTokenRefresh(api, authState);
            return refreshed;
        }
        const req = {
            refresh_token: authState.refreshToken
        };
        const refreshResult = await rawBaseQuery({
            url: "/api/admin/refresh",
            method: "POST",
            body: req
        }, api, {});
        if (refreshResult.data) {
            // Store the new tokens
            const tokens = refreshResult.data;
            api.dispatch(authTokenChange({
                accessToken: tokens.access_token,
                refreshToken: tokens.refresh_token
            }));
            refreshed = true;
        } else {
            // If refresh fails, log the user out.
            api.dispatch(logoutUser());
        }
    } finally{
        // Release the mutex lock.
        release();
    }
    return refreshed;
};
/**
 * Exchange an expired Keycloak access token for a fresh one using the
 * Keycloak token endpoint with `grant_type=refresh_token`.
 *
 * On success the new access + refresh tokens are stored in Redux and
 * the user's identity/permissions are re-resolved via `/auth/me`.
 *
 * Returns `true` if the refresh succeeded, `false` (+ logout) otherwise.
 */ async function handleKeycloakTokenRefresh(api, authState) {
    try {
        // The OIDC public config was persisted to localStorage during login so
        // we can reach Keycloak's token endpoint without depending on RTK Query.
        const raw = localStorage.getItem("oidcPublicConfig");
        if (!raw || !authState.refreshToken) {
            api.dispatch(logoutUser());
            return false;
        }
        const oidcConfig = JSON.parse(raw);
        const tokenUrl = `${oidcConfig.server_url.replace(/\/+$/, "")}/realms/${oidcConfig.realm}/protocol/openid-connect/token`;
        const body = new URLSearchParams({
            grant_type: "refresh_token",
            client_id: oidcConfig.auth_client_id,
            refresh_token: authState.refreshToken
        });
        const response = await fetch(tokenUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            },
            body
        });
        if (!response.ok) {
            console.error("Keycloak token refresh failed:", response.status);
            api.dispatch(logoutUser());
            return false;
        }
        const data = await response.json();
        const newAccessToken = data.access_token;
        const newRefreshToken = data.refresh_token;
        // Store the refreshed Keycloak tokens
        api.dispatch(authTokenChange({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken
        }));
        // Re-resolve the user's identity and permissions from the backend.
        // The middleware validates the new Keycloak JWT and returns the Payload.
        try {
            const meResponse = await fetch("/api/admin/auth/me", {
                headers: {
                    Authorization: `Bearer ${newAccessToken}`
                }
            });
            if (meResponse.ok) {
                const payload = await meResponse.json();
                api.dispatch(setUserDetails({
                    user: payload.user,
                    roles: payload.roles ?? [],
                    permissions: payload.permissions ?? [],
                    mustChangePassword: payload.must_change_password ?? false
                }));
            }
        } catch  {
        // /auth/me failure is non-fatal — the token itself is already refreshed
        }
        return true;
    } catch (e) {
        console.error("Keycloak token refresh error:", e);
        api.dispatch(logoutUser());
        return false;
    }
}
export const performLogin = async (login, dispatch, credentials)=>{
    const result = await login(credentials).unwrap();
    // Store access and refresh tokens in Redux store
    dispatch(authTokenChange({
        accessToken: result.access_token,
        refreshToken: result.refresh_token
    }));
    // Parse user details and store in Redux
    const claims = JSON.parse(atob(result.access_token.split(".")[1]));
    const currentUser = {
        user: claims.user,
        roles: claims.roles,
        permissions: claims.permissions,
        mustChangePassword: claims.must_change_password ?? false
    };
    dispatch(setUserDetails(currentUser));
};
